/**
 * 分享平台 — Douyin 风格全屏浏览（客户移动端）
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Heart, Star, MessageSquare, X, Search, Bell, LogOut, Maximize, Volume2, VolumeX } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { api, getErrorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import type { PromotionFile } from '../types/api';

const CATEGORIES = ['基材类','油墨涂料类','印刷工艺类','功能类','智能化类','绿色可持续类','大满创新'];
const VIDEO_EXTS = ['mp4','webm','mov','avi'];
const IMG_EXTS = ['jpg','jpeg','png','gif'];

export default function PromotionFeed() {
  const { user, logout } = useAuth();
  const { showToast } = useToast();

  // 文件
  const [files, setFiles] = useState<PromotionFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [edgeMsg, setEdgeMsg] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const videoMap = useRef<Map<number, HTMLVideoElement>>(new Map());

  // 筛选状态
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [watchFilter, setWatchFilter] = useState('all');
  const [likedFiles, setLikedFiles] = useState<Set<number>>(new Set());
  const [favFiles, setFavFiles] = useState<Set<number>>(new Set());
  const [isMuted, setIsMuted] = useState(true);
  const [likeCounts, setLikeCounts] = useState<Record<number,number>>({});
  const [favCounts, setFavCounts] = useState<Record<number,number>>({});
  const [commentCounts, setCommentCounts] = useState<Record<number,number>>({});

  // 切换过渡锁
  const isTransitioning = useRef(false);
  const touchStartY = useRef(0);

  const isVideo = (f: PromotionFile) => VIDEO_EXTS.includes((f.file_type||'').toLowerCase());

  const filtered = files.filter(f => {
    const t = f.title || f.file_name;
    if (searchQuery && !t.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (categoryFilter && f.category !== categoryFilter) return false;
    if (watchFilter==='favorites' && !favFiles.has(f.file_id)) return false;
    if (watchFilter==='watched' && !f.is_watched) return false;
    if (watchFilter==='unwatched' && f.is_watched) return false;
    return true;
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getInviteeFiles();
      const data = res.data || [];
      setFiles(data);
      const lc:Record<number,number>={}, fc:Record<number,number>={}, cc:Record<number,number>={};
      data.forEach((f:any)=>{lc[f.file_id]=f.like_count||0;fc[f.file_id]=f.fav_count||0;cc[f.file_id]=f.comment_count||0;});
      setLikeCounts(lc); setFavCounts(fc); setCommentCounts(cc);
      // 初始化 liked/fav sets
      const liked = new Set<number>(), faved = new Set<number>();
      data.forEach((f:any)=>{ if(f.liked)liked.add(f.file_id); if(f.faved)faved.add(f.file_id); });
      setLikedFiles(liked); setFavFiles(faved);
      // 打乱顺序，优先未观看排前面
      const unwatched = data.filter((f:any)=>!f.is_watched);
      const watched = data.filter((f:any)=>f.is_watched);
      for(let i=unwatched.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[unwatched[i],unwatched[j]]=[unwatched[j],unwatched[i]];}
      for(let i=watched.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[watched[i],watched[j]]=[watched[j],watched[i]];}
      setFiles([...unwatched, ...watched]);
    } catch(e) { showToast('error',getErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // 阻止移动端下拉刷新
  useEffect(() => {
    document.body.style.overscrollBehavior = 'none';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'none';
    const preventPullRefresh = (e: TouchEvent) => {
      if (e.touches.length > 1) return; // 多指不拦截
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const touchY = e.touches[0].clientY;
      if (scrollTop <= 0 && touchY > 0) e.preventDefault();
    };
    document.addEventListener('touchmove', preventPullRefresh, { passive: false });
    return () => {
      document.body.style.overscrollBehavior = '';
      document.body.style.overflow = '';
      document.documentElement.style.overscrollBehavior = '';
      document.removeEventListener('touchmove', preventPullRefresh);
    };
  }, []);

  // 记录观看
  useEffect(() => {
    const f = filtered[currentIndex];
    if (!f) return;
    api.request('/promotions/invitee/files/view-log', {
      method: 'POST',
      body: JSON.stringify({ file_id: f.file_id, view_start: new Date().toISOString(), view_duration: 0 }),
    }).catch(() => {});
    // 标记本地已观看
    setFiles(prev => prev.map(pf => pf.file_id === f.file_id ? { ...pf, is_watched: true } : pf));
  }, [currentIndex]);

  // 滑动切换
  const goTo = (idx: number) => {
    if (idx < 0 || idx >= filtered.length) {
      setEdgeMsg(idx < 0 ? '已到顶部' : '已到底部');
      setTimeout(() => setEdgeMsg(''), 1000);
      return;
    }
    if (isTransitioning.current) return;
    isTransitioning.current = true;
    const cur = videoMap.current.get(filtered[currentIndex]?.file_id);
    if (cur) cur.pause();
    setPausedVid(null);
    setCurrentIndex(idx);
    setTimeout(() => { isTransitioning.current = false; }, 400);
  };

  const onTouchStart = (e: React.TouchEvent) => { touchStartY.current = e.touches[0].clientY; };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (isFullscreen) return;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dy) > 60) { if (dy < 0) goTo(currentIndex + 1); else goTo(currentIndex - 1); }
  };
  const handleTogglePlay = (e: React.MouseEvent, f: PromotionFile) => {
    e.stopPropagation();
    if (isTransitioning.current) return;
    const vid = videoMap.current.get(f.file_id);
    if (!vid) return;
    if (vid.paused) { vid.play().catch(()=>{}); setPausedVid(null); }
    else { vid.pause(); setPausedVid(f.file_id); }
  };

  // 自定义全屏（不旋转屏幕）
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFullscreen(p => !p);
  };

  // 暂停/播放状态
  const [pausedVid, setPausedVid] = useState<number|null>(null);

  // 进度条
  const [progressHover, setProgressHover] = useState<number|null>(null);
  useEffect(() => {
    const f = filtered[currentIndex];
    if (!f || !isVideo(f)) return;
    const vid = videoMap.current.get(f.file_id);
    const bar = document.querySelector(`[data-progress="${f.file_id}"]`) as HTMLDivElement|null;
    const dot = document.querySelector(`[data-dot="${f.file_id}"]`) as HTMLDivElement|null;
    if (!vid || !bar) return;
    const tick = () => {
      if (!vid.duration) return;
      const pct = vid.currentTime/vid.duration*100;
      bar.style.width = pct+'%';
      if (dot) { dot.style.left = pct+'%'; dot.style.opacity = progressHover===f.file_id?'1':'0'; }
    };
    vid.addEventListener('timeupdate', tick);
    vid.addEventListener('loadedmetadata', tick);
    const id = setInterval(tick, 200);
    return () => { clearInterval(id); vid.removeEventListener('timeupdate',tick); vid.removeEventListener('loadedmetadata',tick); };
  }, [currentIndex, filtered, progressHover]);

  const seekProgress = (e: React.MouseEvent|React.TouchEvent, fid: number) => {
    e.stopPropagation();
    const wrapper = document.querySelector(`[data-barwrap="${fid}"]`) as HTMLDivElement|null;
    const vid = videoMap.current.get(fid);
    if (!wrapper || !vid || !vid.duration) return;
    const rect = wrapper.getBoundingClientRect();
    const cx = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const pct = Math.max(0, Math.min(1, (cx - rect.left - 12) / (rect.width - 24)));
    vid.currentTime = pct * vid.duration;
  };

  // 互动
  const toggleLike = async (fid: number) => {
    const liked = likedFiles.has(fid);
    setLikedFiles(p => { const n = new Set(p); liked ? n.delete(fid) : n.add(fid); return n; });
    setLikeCounts(p => ({ ...p, [fid]: (p[fid] || 0) + (liked ? -1 : 1) }));
    try { await api.request(`/promotions/invitee/files/${fid}/like`, { method: 'POST' }); }
    catch { setLikedFiles(p => { const n = new Set(p); liked ? n.add(fid) : n.delete(fid); return n; }); setLikeCounts(p => ({ ...p, [fid]: (p[fid] || 0) + (liked ? 1 : -1) })); }
  };

  const toggleFav = async (fid: number) => {
    const faved = favFiles.has(fid);
    setFavFiles(p => { const n = new Set(p); faved ? n.delete(fid) : n.add(fid); return n; });
    setFavCounts(p => ({ ...p, [fid]: (p[fid] || 0) + (faved ? -1 : 1) }));
    try { await api.request(`/promotions/invitee/files/${fid}/fav`, { method: 'POST' }); }
    catch { setFavFiles(p => { const n = new Set(p); faved ? n.add(fid) : n.delete(fid); return n; }); setFavCounts(p => ({ ...p, [fid]: (p[fid] || 0) + (faved ? 1 : -1) })); }
  };

  // 评论
  const [showC, setShowC] = useState(false);
  const [cFile, setCFile] = useState<PromotionFile|null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [cText, setCText] = useState('');
  const [cLoading, setCLoading] = useState(false);

  const openComments = async (f: PromotionFile) => {
    setCFile(f); setShowC(true); setComments([]); setCLoading(true);
    try { const r = await api.request<any[]>('/promotions/invitee/files/'+f.file_id+'/comments'); setComments(r.data||[]); }
    catch { setComments([]); }
    finally { setCLoading(false); }
  };
  const postComment = async () => {
    if (!cText.trim() || !cFile) return;
    try { await api.request('/promotions/invitee/files/'+cFile.file_id+'/comments',{method:'POST',body:JSON.stringify({content:cText})}); showToast('success','已提交'); setCText(''); openComments(cFile); }
    catch(e) { showToast('error',getErrorMessage(e)); }
  };

  // 筛选面板
  const [showFilter, setShowFilter] = useState(false);

  // 留言
  const [showMsg, setShowMsg] = useState(false);
  const [messages, setMessages] = useState<any[]>([]);
  const [msgText, setMsgText] = useState('');
  const [msgSending, setMsgSending] = useState(false);
  const [msgLoading, setMsgLoading] = useState(false);

  const fetchMessages = async () => {
    setMsgLoading(true);
    try { const r = await api.request<any[]>('/promotions/message-board'); setMessages(r.data||[]); }
    catch { setMessages([]); }
    finally { setMsgLoading(false); }
  };
  const sendMessage = async () => {
    if (!msgText.trim()) return;
    setMsgSending(true);
    try { await api.request('/promotions/message-board',{method:'POST',body:JSON.stringify({content:msgText})}); showToast('success','已提交'); setMsgText(''); fetchMessages(); }
    catch(e) { showToast('error',getErrorMessage(e)); }
    finally { setMsgSending(false); }
  };

  // 铃铛
  const [showBell, setShowBell] = useState(false);
  const [notis, setNotis] = useState<any[]>([]);
  const [notiFilter, setNotiFilter] = useState('all');
  const [notiLoading, setNotiLoading] = useState(false);
  const [readIds, setReadIds] = useState<Set<number|string>>(() => {
    try { const raw = JSON.parse(localStorage.getItem('noti_read')||'[]'); if (raw.length && typeof raw[0]==='number') return new Set(); return new Set(raw); }
    catch { return new Set(); }
  });
  const fetchNotis = async () => {
    setNotiLoading(true);
    try { const r = await api.request<any[]>('/promotions/customer-messages'); setNotis(r.data||[]); }
    catch { setNotis([]); }
    finally { setNotiLoading(false); }
  };
  useEffect(() => { fetchNotis(); }, []);
  useEffect(() => { fetchNotis(); }, []);

  const closeBell = () => {
    const ids = notis.map((n:any)=>n.id);
    if (ids.length>0) { setReadIds(p => { const n=new Set(p); ids.forEach((id:any)=>n.add(id)); localStorage.setItem('noti_read',JSON.stringify([...n])); return n; }); }
    setShowBell(false);
  };

  return (
    <div style={{height:'100dvh',width:'100vw',background:'#000',overflow:'hidden',position:'fixed',inset:0,overscrollBehavior:'none',WebkitOverflowScrolling:'auto'}}>
      {/* 顶部 */}
      {!isFullscreen && <div style={{position:'absolute',top:0,left:0,right:0,zIndex:500,padding:'12px 16px',paddingTop:'max(12px,env(safe-area-inset-top))',display:'flex',alignItems:'center',gap:12,background:'linear-gradient(180deg,rgba(0,0,0,.6) 0%,transparent 100%)'}}>
        <div style={{position:'relative'}}>
          <button onClick={()=>showBell?closeBell():(setShowBell(true),fetchNotis())} style={{background:'none',border:'none',color:'#fff',cursor:'pointer',padding:6}}><Bell size={22} strokeWidth={2.5}/>
            {notis.filter((n:any)=>!readIds.has(n.id)).length>0 && <span style={{position:'absolute',top:2,right:2,background:'#ef4444',color:'#fff',fontSize:10,minWidth:18,height:18,borderRadius:9,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:600}}>{notis.filter((n:any)=>!readIds.has(n.id)).length}</span>}
          </button>
          {showBell && <>
            <div onClick={closeBell} style={{position:'fixed',inset:0,zIndex:999}}></div>
            <div style={{position:'fixed',top:60,right:12,width:320,maxHeight:380,overflow:'auto',background:'#fff',borderRadius:14,boxShadow:'0 16px 48px rgba(0,0,0,.2)',zIndex:1000,color:'#333',fontSize:'.8125rem'}}>
              <div style={{padding:'14px 18px',fontWeight:600,borderBottom:'1px solid #eee',display:'flex',justifyContent:'space-between',position:'sticky',top:0,background:'#fff'}}><span>消息中心</span><button onClick={fetchNotis} style={{background:'none',border:'none',fontSize:'.75rem',color:'#999',cursor:'pointer'}}>刷新</button></div>
              <div style={{padding:'6px 14px',display:'flex',gap:6,borderBottom:'1px solid #eee'}}>
                {[{key:'all',label:'全部'},{key:'message_reply',label:'留言回复'},{key:'comment_reply',label:'评论回复'}].map(cat => {
                  const count = cat.key==='all' ? notis.filter((n:any)=>!readIds.has(n.id)).length
                    : notis.filter((n:any)=>n.type===cat.key&&!readIds.has(n.id)).length;
                  return (<button key={cat.key} onClick={()=>setNotiFilter(cat.key)} style={{
                    padding:'4px 12px',borderRadius:16,border:'none',cursor:'pointer',fontSize:'.75rem',
                    background:notiFilter===cat.key?'var(--cinnabar-500)':'#f3f4f6',
                    color:notiFilter===cat.key?'#fff':'var(--ink-500)',
                    fontWeight:notiFilter===cat.key?600:400,display:'flex',alignItems:'center',gap:4,
                  }}>{cat.label}{count>0&&<span style={{background:notiFilter===cat.key?'rgba(255,255,255,.3)':'#dc2626',color:'#fff',fontSize:10,minWidth:16,height:16,borderRadius:8,display:'inline-flex',alignItems:'center',justifyContent:'center'}}>{count>99?'99+':count}</span>}</button>);
                })}
              </div>
              {notiLoading?<div style={{padding:30,textAlign:'center',color:'#999'}}>加载中...</div>:
               (()=>{const filtered=notiFilter==='all'?notis:notis.filter((n:any)=>n.type===notiFilter);return filtered.length===0?<div style={{padding:30,textAlign:'center',color:'#999'}}>暂无消息</div>:
               filtered.map((n:any,i:number)=>{const u=!readIds.has(n.id);
                return (<div key={i} style={{padding:'12px 18px',borderBottom:'1px solid #f3f4f6',background:u?'#dbeafe':'#f3f4f6',borderLeft:u?'3px solid #3b82f6':'3px solid transparent'}}>
                  <div style={{color:'#333'}}>{n.type==='message_reply'?`留言：${n.content||''}`:`评论：${n.content||''}`}</div>
                  {n.reply&&<div style={{color:'#16a34a',marginTop:4}}>回复：{n.reply}</div>}
                  <div style={{display:'flex',alignItems:'center',gap:8,marginTop:4}}><span style={{color:'#999',fontSize:'.6875rem'}}>{new Date(n.create_time).toLocaleString('zh-CN')}</span>{u&&<span style={{fontSize:'.625rem',color:'#3b82f6',background:'#dbeafe',padding:'1px 5px',borderRadius:3}}>未读</span>}</div>
                </div>);})})()}
            </div>
          </>}
        </div>
        <button onClick={()=>{setShowMsg(true);fetchMessages();}} style={{background:'none',border:'none',color:'#fff',cursor:'pointer',padding:6}}><MessageSquare size={22} strokeWidth={2.5}/></button>
        <div style={{flex:1}}></div>
        <button onClick={()=>setShowFilter(true)} style={{background:'rgba(255,255,255,.15)',border:'none',color:'#fff',cursor:'pointer',padding:'6px 12px',borderRadius:16,fontSize:'.75rem',display:'flex',alignItems:'center',gap:4}}><Search size={14}/> 筛选</button>
        <button onClick={logout} style={{background:'none',border:'none',color:'#fff',cursor:'pointer',padding:6}}><LogOut size={20} strokeWidth={2.5}/></button>
      </div>}

      {/* 边缘提示 */}
      {edgeMsg && <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',zIndex:100,background:'rgba(0,0,0,.7)',color:'#fff',padding:'8px 20px',borderRadius:20,fontSize:'.8125rem',fontWeight:500}}>{edgeMsg}</div>}

      {/* 视频列表 */}
      {loading ? <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',color:'#fff'}}>加载中...</div> :
       filtered.length===0 ? <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',color:'#999'}}>暂无内容</div> :
       <div ref={containerRef} style={{height:'100%',overflow:'hidden',position:'relative'}} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
         {filtered.map((f,i) => {
           const cur = i === currentIndex;
           return (
           <div key={f.file_id} style={{height:'100dvh',width:'100%',position:'absolute',top:0,left:0,transform:`translateY(${(i-currentIndex)*100}dvh)`,transition:'transform .35s cubic-bezier(.25,.46,.45,.94)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:isFullscreen&&cur?501:0}}>
             {isVideo(f)
               ? <div style={{position:'relative',width:'100%',height:'100%'}}>
                    <video ref={el=>{if(el){videoMap.current.set(f.file_id,el);if(cur&&pausedVid!==f.file_id){const p=el.play();if(p!==undefined)p.catch(()=>{});}else if(!cur||pausedVid===f.file_id){el.pause();}}}} src={(f.preview_url||'').replace('/minio/','/internal-minio/')} muted={isMuted} loop playsInline controlsList="nodownload" onContextMenu={e=>e.preventDefault()} preload={Math.abs(i-currentIndex)<=1?"auto":"none"} style={{width:'100%',height:'100%',objectFit:'contain'}} />
                    <div onClick={e=>handleTogglePlay(e,f)} style={{position:'absolute',inset:0,zIndex:1}}></div>
                  </div>
               : IMG_EXTS.includes((f.file_type||'').toLowerCase())
               ? <img src={(f.preview_url||'').replace('/minio/','/internal-minio/')} alt={f.title} style={{maxWidth:'100%',maxHeight:'100%',objectFit:'contain'}} />
               : <div style={{color:'#fff',textAlign:'center',padding:20}}>{f.title||f.file_name}</div>}

             {cur && !isFullscreen && <>
             </>}

             {/* 全屏 + 进度条（始终可见） */}
             {cur && <>
               {/* 暂停播放按钮（全屏也显示） */}
               {isVideo(f) && pausedVid === f.file_id && (
                 <div style={{position:"absolute",inset:0,zIndex:15,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none"}}>
                   <div style={{width:64,height:64,borderRadius:"50%",background:"rgba(255,255,255,.15)",backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                     <div style={{width:0,height:0,borderTop:"14px solid transparent",borderBottom:"14px solid transparent",borderLeft:"22px solid rgba(255,255,255,.9)",marginLeft:4}}></div>
                   </div>
                 </div>
               )}

               {/* 静音 + 全屏按钮 */}
               {isVideo(f) && (<>
                 <button onClick={e=>{e.stopPropagation();setIsMuted(!isMuted);}} style={{position:"absolute",right:52,bottom:100,zIndex:12,cursor:"pointer"}}>
                   {isMuted ? <VolumeX size={20} stroke="#fff" strokeWidth={2.5}/> : <Volume2 size={20} stroke="#fff" strokeWidth={2.5}/>}
                 </button>
                 <button onClick={e=>{e.stopPropagation();toggleFullscreen(e);}} style={{position:"absolute",right:16,bottom:100,zIndex:12,cursor:"pointer"}}>
                   <Maximize size={20} stroke="#fff" strokeWidth={2.5}/>
                 </button>
               </>)}

               {/* 进度条 — 仿抖音 */}
               {isVideo(f) && (
                 <div data-barwrap={f.file_id} onClick={e=>seekProgress(e,f.file_id)}
                   onMouseEnter={()=>setProgressHover(f.file_id)} onMouseLeave={()=>setProgressHover(null)}
                   onTouchStart={()=>setProgressHover(f.file_id)} onTouchEnd={()=>setTimeout(()=>setProgressHover(null),800)}
                   style={{position:'absolute',bottom:70,left:0,right:0,zIndex:isFullscreen?502:12,height:24,padding:'10px 0',cursor:'pointer'}}>
                   <div style={{height:2,background:'rgba(255,255,255,.2)',borderRadius:1,margin:'0 12px',position:'relative'}}>
                     <div data-progress={f.file_id} style={{height:'100%',background:'#fff',borderRadius:1,width:'0%',position:'relative',transition:'width .15s linear'}}></div>
                     <div data-dot={f.file_id} style={{position:'absolute',left:'0%',top:'50%',transform:'translate(-50%,-50%)',width:12,height:12,borderRadius:'50%',background:'#fff',opacity:0,transition:'opacity .15s',pointerEvents:'none',boxShadow:'0 0 4px rgba(0,0,0,.3)'}}></div>
                   </div>
                 </div>
               )}

               {/* 底部信息 */}
               {!isFullscreen && <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'80px 16px 24px',background:'linear-gradient(0deg,rgba(0,0,0,.7) 0%,transparent 100%)',zIndex:10}}>
                 {f.category && <span style={{padding:'2px 8px',borderRadius:4,background:'rgba(255,255,255,.2)',color:'#fff',fontSize:'.6875rem',marginBottom:6,display:'inline-block'}}>{f.category}</span>}
                 <div style={{fontSize:'1rem',fontWeight:600,color:'#fff',marginBottom:4}}>{f.title||f.file_name}</div>
                 {f.description && <div style={{fontSize:'.75rem',color:'rgba(255,255,255,.7)',lineHeight:1.4}}>{f.description.slice(0,100)}</div>}
               </div>}

               {/* 右侧互动 */}
               {!isFullscreen && <div style={{position:'absolute',right:12,bottom:150,zIndex:11,display:'flex',flexDirection:'column',alignItems:'center',gap:24}}>
                 <button onClick={e=>{e.stopPropagation();toggleLike(f.file_id);}} style={{background:'none',border:'none',cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                   <Heart size={34} fill={likedFiles.has(f.file_id)?'#ef4444':'none'} stroke={likedFiles.has(f.file_id)?'#ef4444':'#fff'} strokeWidth={likedFiles.has(f.file_id)?0:1.5}/>
                   <span style={{color:'#fff',fontSize:'.6875rem',fontWeight:500}}>{likeCounts[f.file_id]||0}</span>
                 </button>
                 <button onClick={e=>{e.stopPropagation();toggleFav(f.file_id);}} style={{background:'none',border:'none',cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                   <Star size={34} fill={favFiles.has(f.file_id)?'#eab308':'none'} stroke={favFiles.has(f.file_id)?'#eab308':'#fff'} strokeWidth={favFiles.has(f.file_id)?0:1.5}/>
                   <span style={{color:'#fff',fontSize:'.6875rem',fontWeight:500}}>{favCounts[f.file_id]||0}</span>
                 </button>
                 <button onClick={e=>{e.stopPropagation();openComments(f);}} style={{background:'none',border:'none',cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                   <MessageSquare size={34} stroke="#fff" strokeWidth={2.5}/>
                   <span style={{color:'#fff',fontSize:'.6875rem',fontWeight:500}}>{commentCounts[f.file_id]||0}</span>
                 </button>
               </div>}
             </>}
           </div>);
         })}
       </div>}

      {/* 自定义全屏：当前视频浮到最顶层 */}
      {isFullscreen && (
        <div onClick={toggleFullscreen} style={{position:"fixed",inset:0,zIndex:500,background:"#000"}}>
          <button onClick={toggleFullscreen} style={{position:"absolute",top:16,left:16,zIndex:501,background:"rgba(0,0,0,.5)",border:"none",borderRadius:20,padding:8,display:"flex",cursor:"pointer"}}><X size={20} stroke="#fff" strokeWidth={2}/></button>
        </div>
      )}

      {/* 筛选面板 */}
      {showFilter && <>
        <div onClick={()=>setShowFilter(false)} style={{position:'fixed',inset:0,zIndex:199,background:'rgba(0,0,0,.4)'}}></div>
        <div style={{position:'fixed',top:80,left:12,right:12,zIndex:200,background:'#fff',borderRadius:14,padding:16,display:'flex',flexDirection:'column',gap:10}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}><span style={{fontWeight:600,fontSize:'.875rem'}}>筛选</span><button onClick={()=>setShowFilter(false)} style={{background:'none',border:'none',cursor:'pointer'}}><X size={18}/></button></div>
          <input placeholder="搜索视频..." value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} style={{padding:'10px 12px',borderRadius:8,border:'1px solid #e5e7eb',fontSize:'.8125rem'}}/>
          <select value={categoryFilter} onChange={e=>setCategoryFilter(e.target.value)} style={{padding:'8px',borderRadius:8,border:'1px solid #e5e7eb',fontSize:'.8125rem'}}>
            <option value="">全部分类</option>{CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
            {['all','unwatched','watched','favorites'].map(k=><button key={k} onClick={()=>{setWatchFilter(k);setCurrentIndex(0);}} style={{flex:'1 1 calc(50% - 6px)',padding:'8px',borderRadius:8,border:watchFilter===k?'2px solid var(--cinnabar-500)':'1px solid #e5e7eb',background:watchFilter===k?'var(--cinnabar-50)':'#fff',fontSize:'.75rem'}}>{({all:'全部',unwatched:'未观看',watched:'已观看',favorites:'收藏'})[k]}</button>)}
          </div>
          <button onClick={()=>{setSearchQuery('');setWatchFilter('all');setCategoryFilter('');setCurrentIndex(0);setShowFilter(false);}} style={{padding:'10px',borderRadius:8,border:'1px solid #e5e7eb',background:'#f9fafb',fontSize:'.8125rem',cursor:'pointer',color:'#666',textAlign:'center'}}>清除筛选</button>
        </div>
      </>}

      {/* 评论弹窗 */}
      <AnimatePresence>{showC&&cFile&&<motion.div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:300,display:'flex',alignItems:'flex-end',justifyContent:'center'}} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowC(false)}>
        <motion.div style={{background:'#fff',borderRadius:'18px 18px 0 0',width:'100%',maxWidth:500,maxHeight:'80vh',display:'flex',flexDirection:'column'}} initial={{y:'100%'}} animate={{y:0}} exit={{y:'100%'}} onClick={e=>e.stopPropagation()}>
          <div style={{padding:'14px 20px',fontWeight:600,borderBottom:'1px solid #eee',display:'flex',justifyContent:'space-between'}}><span>评论 ({comments.length})</span><button onClick={()=>setShowC(false)} style={{background:'none',border:'none',cursor:'pointer'}}><X size={18}/></button></div>
          <div style={{flex:1,overflow:'auto',padding:'0 20px',maxHeight:400}}>
            {cLoading?<div style={{textAlign:'center',padding:30,color:'#999'}}>加载中...</div>:
             comments.length===0?<div style={{textAlign:'center',padding:30,color:'#999'}}>暂无评论</div>:
             comments.map((c:any)=><div key={c.id} style={{padding:'14px 0',borderBottom:'1px solid #f5f5f5'}}>
               <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                 <span style={{fontSize:'.8125rem',fontWeight:500,color:'#8b8b8b'}}>{c.user_name}</span>
                 {c.user_id===user?.user_id&&<span style={{fontSize:'.625rem',color:'#3b82f6',background:'#dbeafe',padding:'1px 5px',borderRadius:3}}>我</span>}
                 <span style={{fontSize:'.6875rem',color:'#bbb'}}>{new Date(c.create_time).toLocaleString('zh-CN')}</span>
               </div>
               <div style={{fontSize:'.875rem',color:'#222'}}>{c.content}</div>
               {c.reply&&<div style={{marginTop:8,padding:'10px 14px',background:'#f8f8f8',borderRadius:8,fontSize:'.8125rem'}}><span style={{fontWeight:500,color:'#8b8b8b'}}>回复：</span><span style={{color:'#555'}}>{c.reply}</span></div>}
             </div>)}
          </div>
          <div style={{padding:'12px 20px',borderTop:'1px solid #eee',display:'flex',gap:8}}>
            <input value={cText} onChange={e=>setCText(e.target.value)} placeholder="写评论..." style={{flex:1,padding:'10px 14px',borderRadius:10,border:'1px solid #e5e7eb',fontSize:'.875rem'}}/>
            <button onClick={postComment} style={{padding:'8px 16px',borderRadius:8,border:'none',background:'var(--cinnabar-500)',color:'#fff',cursor:'pointer',fontSize:'.8125rem'}}>发送</button>
          </div>
        </motion.div>
      </motion.div>}</AnimatePresence>

      {/* 留言板弹窗 */}
      <AnimatePresence>{showMsg&&<motion.div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',padding:24}} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowMsg(false)}>
        <motion.div style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:500,maxHeight:'80vh',display:'flex',flexDirection:'column'}} initial={{scale:.95}} animate={{scale:1}} onClick={e=>e.stopPropagation()}>
          <div style={{padding:'14px 20px',fontWeight:600,borderBottom:'1px solid #eee',display:'flex',justifyContent:'space-between'}}><span>留言板</span><button onClick={()=>setShowMsg(false)} style={{background:'none',border:'none',cursor:'pointer'}}><X size={18}/></button></div>
          <div style={{flex:1,overflow:'auto',padding:'0 20px',maxHeight:400}}>
            {msgLoading?<div style={{textAlign:'center',padding:30,color:'#999'}}>加载中...</div>:
             messages.length===0?<div style={{textAlign:'center',padding:30,color:'#999'}}>暂无留言</div>:
             messages.map((m:any)=><div key={m.id} style={{padding:'12px 0',borderBottom:'1px solid #f5f5f5'}}>
               <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                 <span style={{display:'flex',alignItems:'center',gap:6}}><strong style={{fontSize:'.8125rem'}}>{m.user_name}</strong>{m.user_id===user?.user_id&&<span style={{fontSize:'.625rem',color:'#3b82f6',background:'#dbeafe',padding:'1px 5px',borderRadius:3}}>我</span>}</span>
                 <span style={{fontSize:'.6875rem',padding:'2px 8px',borderRadius:10,background:m.status===0?'#fef3c7':m.status===1?'#dcfce7':'#f1f5f9',color:m.status===0?'#92400e':m.status===1?'#166534':'#64748b'}}>{['未处理','已回复','已关闭'][m.status]}</span>
               </div>
               <div style={{fontSize:'.8125rem',color:'#666',marginTop:6,whiteSpace:'pre-wrap'}}>{m.content}</div>
               {m.reply&&<div style={{marginTop:8,padding:'10px 14px',background:'#f0fdf4',borderRadius:8,fontSize:'.8125rem'}}><span style={{fontWeight:600,color:'#166534'}}>回复（{m.replied_by}）：</span><div style={{color:'#666',marginTop:4}}>{m.reply}</div></div>}
               <div style={{fontSize:'.6875rem',color:'#bbb',marginTop:4}}>{new Date(m.create_time).toLocaleString('zh-CN')}</div>
             </div>)}
          </div>
          <div style={{padding:'12px 20px',borderTop:'1px solid #eee',display:'flex',gap:8}}>
            <textarea value={msgText} onChange={e=>setMsgText(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendMessage();}}} placeholder="输入留言..." rows={2} style={{flex:1,padding:'10px 14px',borderRadius:10,border:'1px solid #e5e7eb',fontSize:'.8125rem',resize:'none'}}/>
            <button onClick={sendMessage} disabled={msgSending||!msgText.trim()} style={{padding:'8px 20px',borderRadius:8,border:'none',background:'var(--cinnabar-500)',color:'#fff',cursor:'pointer',fontSize:'.8125rem',alignSelf:'flex-end'}}>发送</button>
          </div>
        </motion.div>
      </motion.div>}</AnimatePresence>
    </div>
  );
}
